import React from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'

import { connect } from 'react-redux'
import { Switch, Route, Redirect } from 'react-router-dom'

import { authorized } from 'common/authorizer'
import permissions from 'common/system-permissions'
import collegeFeatures from 'common/college-features'

import { hasPermission } from 'store/user'
import { isFeatureEnabled } from 'store/college'
import { useCanManageRoundsAndRequirements } from 'store/user/hooks'

import rememberedRedirectHelper from './remembered-redirect-helper'
import Dashboard from './routes/dashboard/Dashboard'
import ContentManagement from './routes/content-management'
import Outreach from './routes/outreach'
import EndOfCycleReportRoute from './routes/end-of-cycle-report'
import DailyBatches from './routes/document-batches/DailyBatches'
import Applicants from './routes/document-batches/Applicants'
import DeliveryPreferences from './routes/delivery-preferences/DeliveryPreferences'
import BatchIndexFile from './routes/delivery-preferences/BatchIndexFile'
import IntegrationsOverviewRoute from './routes/integrations/OverviewRoute'
import SftpIntegration from './routes/integrations/SftpIntegration'
import Visits from './routes/visits/Visits'
import Explore from './routes/explore/Explore'
import MessageCenter from './routes/message-center/MessageCenter'
import FourOhFourRoute from './routes/status/FourOhFour'
import ManageUsers from './routes/org/ManageUsers'
import CollegeSearchRoute from './routes/search'
import CollegeDetailsRoute from './routes/college'
import InAppLayout from './routes/InAppLayout'
import MyProfile from './routes/profile/MyProfile'
import ChangePassword from './routes/profile/ChangePassword'
import AppCycleRounds from './routes/application/AppCycleRounds'
import ApplicationExtensionsRoute from './routes/application/Extensions'
import ConfigureApplication from './routes/application/Configure'
import ActivateApplication from 'routes/application/Activate'
import ConfigureCrmIntegration from 'routes/configure-integration'
import ApplicantApplicationsRoute from 'routes/application/Applicants'
import HighSchoolsRoutes from 'routes/highschools'
import { isHighSchoolExplorerEnabled } from 'store/user'

const withManagementAuthorization = authorized([permissions.MANAGE_ADMISSIONS])
const withDocumentAuthorization = authorized([permissions.PERFORM_ADMISSIONS_TASKS])
const withVisitsAuthorization = authorized([permissions.PERFORM_VISIT_MANGEMENT_TASKS])
const withSearchAuthorization = authorized([permissions.PERFORM_COLLEGE_SEARCH])
export const WrappedCollegeSearchRoute = withSearchAuthorization(CollegeSearchRoute)
const WrappedDailyBatchesRoute = withDocumentAuthorization(DailyBatches)
const WrappedApplicationsRoute = withDocumentAuthorization(Applicants)
const WrappedDeliveryPreferencesRoute = withDocumentAuthorization(DeliveryPreferences)
const WrappedConfiguredBatchIndexFileRoute = withDocumentAuthorization(BatchIndexFile)
const WrappedIntegrationsOverviewRoute = withManagementAuthorization(IntegrationsOverviewRoute)
const WrappedSftpIntegrationRoute = withManagementAuthorization(SftpIntegration)
const WrappedManageUsersRoute = withManagementAuthorization(ManageUsers)
const WrappedVisitsRoute = withVisitsAuthorization(Visits)
const WrappedExploreRoute = withVisitsAuthorization(Explore)
const WrappedMessageCenterRoute = withVisitsAuthorization(MessageCenter)
const WrappedHighSchoolsRoutes = withVisitsAuthorization(HighSchoolsRoutes)

export const AuthRoutes = ({
  canManageContent = false,
  canManageOutreach = false,
  supportsContentManagement = false,
  supportsStudentOutreach = false,
  supportsEndOfCycleReport = false,
  supportsCrmIntegration = false,
  showHighSchoolExplorer = false,
  scid,
  ...rest
}) => {
  const ROOT_PATH = import.meta.env.BASE_URL
  const canManageRounds = useCanManageRoundsAndRequirements()
  if (rememberedRedirectHelper.hasRememberedRoute()) {
    rememberedRedirectHelper.handleRemembered()
    return null
  }
  return (
    <InAppLayout {...rest}>
      <Switch>
        <Redirect
          from={`${ROOT_PATH}/home`}
          to={`${ROOT_PATH}/dashboard`}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/documents`}
          to={`${ROOT_PATH}/documents/batches/daily`}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/message-center`}
          to={`${ROOT_PATH}/message-center/messages`}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/recruit`}
          to={showHighSchoolExplorer ? `${ROOT_PATH}/recruit/highschools` : `${ROOT_PATH}/recruit/explore`}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/documents/integrations`}
          to={`${ROOT_PATH}/documents/integrations/config/overview`}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/colleges`}
          to={`${ROOT_PATH}/colleges/${scid}`}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/engage`}
          to={`${ROOT_PATH}/engage/presence`}
        />
        <Redirect
          from={`${ROOT_PATH}/integrations`}
          to={`${ROOT_PATH}/documents/integrations/config/overview`}
        />
        <Redirect
          from={`${ROOT_PATH}/engage/content-management`}
          to={`${ROOT_PATH}/engage/presence`}
        />
        <Redirect
          from={`${ROOT_PATH}/content-management`}
          to={`${ROOT_PATH}/engage/presence`}
        />
        <Redirect
          from={`${ROOT_PATH}/outreach`}
          to={`${ROOT_PATH}/engage/messaging`}
        />
        <Redirect
          from={`${ROOT_PATH}/engage/outreach`}
          to={`${ROOT_PATH}/engage/messaging`}
        />
        <Route
          path={`${ROOT_PATH}/dashboard`}
          component={Dashboard}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/application`}
          to={`${ROOT_PATH}/application/${canManageRounds ? 'applicants' : 'rounds'}`}
        />
        <Route
          exact
          path={`${ROOT_PATH}/application/applicants`}
          component={ApplicantApplicationsRoute}
        />
        <Route
          exact
          path={`${ROOT_PATH}/application/rounds`}
          component={AppCycleRounds}
        />
        <Route
          exact
          path={`${ROOT_PATH}/application/extensions`}
          component={ApplicationExtensionsRoute}
        />
        <Route
          path={`${ROOT_PATH}/application/settings`}
          component={ConfigureApplication}
        />
        <Route
          path={`${ROOT_PATH}/application/integration`}
          component={ActivateApplication}
        />
        <Route
          path={`${ROOT_PATH}/colleges/search`}
          render={(props) => {
            return <WrappedCollegeSearchRoute {...props} />
          }}
        />
        <Route
          path={`${ROOT_PATH}/colleges/:scid/:section?`}
          render={(props) => {
            const { match } = props
            const { params } = match
            const { scid } = params
            return (
              <CollegeDetailsRoute
                {...props}
                url={match.url}
                section={params.section}
                scid={parseInt(scid, 10)}
              />
            )
          }}
        />
        <Route
          exact
          path={`${ROOT_PATH}/documents/batches/daily`}
          render={(props) => <WrappedDailyBatchesRoute {...props} />}
        />
        <Route
          exact
          path={`${ROOT_PATH}/documents/batches/applicants`}
          render={(props) => <WrappedApplicationsRoute {...props} />}
        />
        <Route
          exact
          path={`${ROOT_PATH}/documents/delivery/preferences`}
          render={(props) => <WrappedDeliveryPreferencesRoute {...props} />}
        />
        <Route
          exact
          path={`${ROOT_PATH}/documents/integrations/config/sftp`}
          render={(props) => <WrappedSftpIntegrationRoute {...props} />}
        />
        <Route
          exact
          path={`${ROOT_PATH}/documents/integrations/config/overview`}
          render={(props) => <WrappedIntegrationsOverviewRoute {...props} />}
        />
        <Route
          exact
          path={`${ROOT_PATH}/documents/delivery/preferences/batch-index-file`}
          render={(props) => <WrappedConfiguredBatchIndexFileRoute {...props} />}
        />
        <Route
          exact
          path={`${ROOT_PATH}/org/users`}
          render={(props) => <WrappedManageUsersRoute {...props} />}
        />
        <Route
          exact
          path={`${ROOT_PATH}/change-password`}
          render={(props) => <ChangePassword {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/my/profile`}
          render={(props) => <MyProfile {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/recruit/visits`}
          render={(props) => <WrappedVisitsRoute {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/recruit/explore`}
          render={(props) => <WrappedExploreRoute {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/recruit/highschools`}
          render={(props) => <WrappedHighSchoolsRoutes {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/message-center`}
          render={(props) => <WrappedMessageCenterRoute {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/engage/presence`}
          render={(props) => (
            <ContentManagement
              {...props}
              canManageContent={canManageContent}
              supportsContentManagement={supportsContentManagement}
            />
          )}
        />
        {supportsCrmIntegration && (
          <Route
            path={`${ROOT_PATH}/engage/integrations`}
            component={ConfigureCrmIntegration}
          />
        )}
        {supportsCrmIntegration && (
          <Redirect
            exact
            from={`${ROOT_PATH}/engage`}
            to={`${ROOT_PATH}/engage/integrations`}
          />
        )}
        <Route
          path={`${ROOT_PATH}/engage/messaging`}
          render={(props) => (
            <Outreach
              {...props}
              canManageOutreach={canManageOutreach}
              supportsStudentOutreach={supportsStudentOutreach}
            />
          )}
        />
        {supportsEndOfCycleReport && (
          <Route
            path={`${ROOT_PATH}/engage/end-of-cycle-report`}
            render={(props) => (
              <EndOfCycleReportRoute
                {...props}
                mountPath={get(props, 'match.path')}
              />
            )}
          />
        )}
        {supportsEndOfCycleReport && (
          <Redirect
            exact
            from={`${ROOT_PATH}/engage`}
            to={`${ROOT_PATH}/engage/end-of-cycle-report`}
          />
        )}
        <FourOhFourRoute />
      </Switch>
    </InAppLayout>
  )
}

AuthRoutes.propTypes = {
  scid: PropTypes.number.isRequired,
  canManageContent: PropTypes.bool.isRequired,
  canManageOutreach: PropTypes.bool.isRequired,
  supportsEndOfCycleReport: PropTypes.bool,
  supportsCrmIntegration: PropTypes.bool,
}

export default connect((state) => ({
  scid: state.college.record.Scid,
  supportsContentManagement: isFeatureEnabled(state, collegeFeatures.SUPPORTS_CONTENT_MANAGEMENT),
  canManageContent: hasPermission(state, permissions.MANAGE_COLLEGE_CONTENT),
  supportsStudentOutreach: isFeatureEnabled(state, collegeFeatures.SUPPORTS_STUDENT_OUTREACH),
  canManageOutreach: hasPermission(state, permissions.MANAGE_COLLEGE_OUTREACH_MESSAGES),
  supportsEndOfCycleReport:
    isFeatureEnabled(state, collegeFeatures.SUPPORTS_CRM_MATCHING_REPORT) &&
    hasPermission(state, permissions.MANAGE_CRM_UPLOADS),
  supportsCrmIntegration: isFeatureEnabled(state, collegeFeatures.CRM_INTEGRATION),
  showHighSchoolExplorer: isHighSchoolExplorerEnabled(state),
}))(AuthRoutes)
